import React from 'react';
import { cn } from '@/lib/utils';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'elevated' | 'bordered';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  interactive?: boolean;
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', padding = 'md', interactive = false, style, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'rounded-lg transition-all duration-200 flex flex-col',
          {
            // Variants
            'bg-card text-card-foreground': variant === 'default' || variant === 'elevated',
            'border border-border': variant === 'default' || variant === 'bordered',
            'bg-transparent': variant === 'bordered',
            
            // Padding
            'p-0': padding === 'none',
            'p-4': padding === 'sm',
            'p-6': padding === 'md',
            'p-8': padding === 'lg',
            
            // Interactive states
            'hover:border-border/80 cursor-pointer': interactive,
          },
          className
        )}
        style={{
          boxShadow: variant === 'bordered' ? undefined : '0 4px 12px #161a2510',
          ...(interactive && {
            transition: 'all 0.2s, box-shadow 0.2s',
          }),
          ...style,
        }}
        {...props}
      />
    );
  }
);

Card.displayName = 'Card';

interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  noPadding?: boolean;
}

export const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, noPadding = false, children, ...props }, ref) => {
    // Check if children contain any non-title/description elements
    const hasActions = React.Children.toArray(children).some(
      child => React.isValidElement(child) && 
      child.type !== CardTitle && 
      child.type !== CardDescription
    );

    return (
      <div
        ref={ref}
        className={cn(
          hasActions ? 'flex items-start justify-between' : '',
          !noPadding && 'pb-4',
          className
        )}
        {...props}
      >
        {hasActions ? children : (
          <div>{children}</div>
        )}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

export const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, as: Component = 'h3', ...props }, ref) => {
    return (
      <Component
        ref={ref}
        className={cn('text-lg font-semibold leading-none tracking-tight font-heading', className)}
        {...props}
      />
    );
  }
);

CardTitle.displayName = 'CardTitle';

interface CardDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {}

export const CardDescription = React.forwardRef<HTMLParagraphElement, CardDescriptionProps>(
  ({ className, ...props }, ref) => {
    return (
      <p
        ref={ref}
        className={cn('text-sm text-muted-foreground mt-1', className)}
        {...props}
      />
    );
  }
);

CardDescription.displayName = 'CardDescription';

interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  noPadding?: boolean;
}

export const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, noPadding = false, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex-1',
          !noPadding && 'py-4',
          className
        )}
        {...props}
      />
    );
  }
);

CardContent.displayName = 'CardContent';

interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  noPadding?: boolean;
}

export const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, noPadding = false, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center',
          !noPadding && 'pt-4',
          className
        )}
        {...props}
      />
    );
  }
);

CardFooter.displayName = 'CardFooter';

// Expandable card content with animation
interface CardExpandableProps extends React.HTMLAttributes<HTMLDivElement> {
  isExpanded: boolean;
}

export const CardExpandable = React.forwardRef<HTMLDivElement, CardExpandableProps>(
  ({ className, isExpanded, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'grid transition-all duration-300 ease-in-out',
          isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
          className
        )}
        {...props}
      >
        <div className="overflow-hidden">
          {children}
        </div>
      </div>
    );
  }
);

CardExpandable.displayName = 'CardExpandable';
